import _, { get, unionBy } from 'lodash';
import uiSelector from 'services/UI/selectors';
import shopSelectors from 'services/shop/selectors';
import { RootState } from 'store';
import { useAppSelector } from 'store/hooks';
import { IDateRangeItem } from '../utils/getRangeDates';
import {
  CALENDAR_ITEM_TYPES,
  CALENDAR_VIEW_TYPE,
  PATH_LOADING,
} from './constants';
import { IApiGetAppointments, IApiGetTableAppointment } from './types/api';
import {
  IAppointmentItemData,
  IStaffAppointmentItemData,
} from './types/appointment';
import { ICalendarItemData } from './types/calendar';
import { IServiceItemData } from 'services/shop/types/categories';
import { specialServiceByStaffsSelect } from './adapters/service';

type MyState = RootState['appointment'];

export const getCurrentState = (state: RootState): MyState =>
  state['appointment'];

const selector = <T = MyState>(key: keyof T, defaultValue?: any) =>
  useAppSelector((state) => get(getCurrentState(state), key, defaultValue));

export const getPureParams = (root: RootState) => {
  const state = getCurrentState(root);
  return state.params ?? {};
};

export const getPureTableParams = (root: RootState) => {
  const state = getCurrentState(root);
  return state.table.params ?? {};
};

const getParams = () => selector('params') as IApiGetAppointments;

const getStaffAppointments = () =>
  selector('staffAppointments') as IStaffAppointmentItemData[];

const getAppointmentsLoading = () =>
  uiSelector.getLoading(PATH_LOADING.getAppointments) as boolean;

const getDateRanges = () => selector('dateRanges') as IDateRangeItem[];

const getDistanceTimeline = () =>
  selector('distanceTimeline') as MyState['distanceTimeline'];

const getViewType = () => selector('viewType') as MyState['viewType'];

const getDateRangeList = () =>
  selector('dateRangeList') as MyState['dateRangeList'];
const getStaffPage = () => (selector('staffListPage') as number) ?? 0;
const getStaffLimit = () => (selector('staffLimit') as number) ?? 0;
const getStaffTotalPage = () => (selector('staffTotalPage') as number) ?? 0;
const getStaffList = () => {
  const data = shopSelectors.data.staffs();
  const page = getStaffPage();
  const limit = appointmentSelectors.getStaffLimit();

  return _(data)
    .drop((page - 1) * limit)
    .take(limit)
    .value();
};

const getAppointmentList = () =>
  selector('appointmentList') as MyState['appointmentList'];

const table = {
  appointments: () => selector('table.data') as IAppointmentItemData[],
  params: () => selector('table.params') as IApiGetTableAppointment,
  totalElements: () => selector('table.totalElements') as number,
  loading: () =>
    uiSelector.getLoading(PATH_LOADING.getTableAppointments) as boolean,
  getParamKey: <Type = any>(key: keyof IApiGetTableAppointment) =>
    selector(`table.params.${key}`) as Type,
};

const getCalendarLoading = () => {
  const appointmentLoading = uiSelector.getLoading(
    PATH_LOADING.getAppointments
  );
  return appointmentLoading;
};

const getLockBreakTimes = () =>
  selector('lockBreakTimes') as MyState['lockBreakTimes'];
const getLockBreakTimesForQuickBooking = () =>
  selector(
    'lockBreakTimesForQuickBooking'
  ) as MyState['lockBreakTimesForQuickBooking'];

const getAppointmentLayout = () =>
  selector('appointmentLayout') as MyState['appointmentLayout'];

const getSelectedCalendarItemTypes = () =>
  selector('selectedCalendarItemTypes') as MyState['selectedCalendarItemTypes'];
const getAppointmentStatusFilter = () =>
  selector('appointmentStatus') as MyState['appointmentStatus'];

const getCalendarItems = () => {
  const state = useAppSelector(state => state.appointment);
  let dataLockBreakTimes: ICalendarItemData[] = [];
  if (state.viewType === CALENDAR_VIEW_TYPE.DAY_VIEW) {
    dataLockBreakTimes = state.lockBreakTimes?.map((o) => {
      return {
        data: o,
        endTime: o.endTime,
        startTime: o.startTime,
        id: o.id,
        staffId: o.staffId,
        type:
          o.type === 'BREAK'
            ? CALENDAR_ITEM_TYPES.BREAK_STAFF
            : CALENDAR_ITEM_TYPES.BLOCK_HOURS,
      };
    });
  }

  const dataAppointments: ICalendarItemData[] = state.appointmentList
    ?.filter((o) => state.appointmentStatus.includes(o.status))
    ?.map((o) => {
      return {
        data: o,
        endTime: o.endTime,
        startTime: o.startTime,
        id: o.appointmentId,
        staffId: o.staffId,
        type: CALENDAR_ITEM_TYPES.APPOINTMENT,
      };
    });

  const result = unionBy(dataLockBreakTimes, dataAppointments, 'id') ?? [];

  return result;
};

const getCategories = () => shopSelectors.data.categories();

const getAppointmentDetailData = () =>
  selector('detail.data') as IAppointmentItemData | null;

const getAppointmentDetailServices = () =>
  selector('detail.services') as IAppointmentItemData['services'];

const getNewAppointmentDraftData = () =>
  selector('newAppointmentDraftData') as MyState['newAppointmentDraftData'];

const getNewBlockHourDraftData = () =>
  selector('newBlockHourDraftData') as MyState['newBlockHourDraftData'];

const getNewBreakTimeDraftData = () =>
  selector('newBreakTimeDraftData') as MyState['newBreakTimeDraftData'];

const getDraftAppointmentDetail = () =>
  selector('draftAppointmentData') as MyState['draftAppointmentData'];

const newAptState = {
  services: () => selector('newAptState.services') as IServiceItemData[],
};

const anybodyBox = {
  open: () => selector('anybodyBox.open') as MyState['anybodyBox']['open'],
  data: () => selector('anybodyBox.data') as MyState['anybodyBox']['data'],
};

const getListStaffAvailableBooking = () => {
  const state = useAppSelector(getCurrentState);
  if (state.viewType === CALENDAR_VIEW_TYPE.DAY_VIEW)
    return state.availableStaffs;
  return state.staffs;
};

const listStaffsOff = () =>
  selector('listStaffsOff') as MyState['listStaffsOff'];

const syncTimeItem = () => selector('syncTimeItem') as MyState['syncTimeItem'];

const loadingListStaffsOff = () =>
  uiSelector.getLoading(PATH_LOADING.listStaffsOffLoading) as boolean;

const multipleDetail = () =>
  selector('multipleDetail') as MyState['multipleDetail'];

const staffCategories = () =>
  selector('staffCategories') as MyState['staffCategories'];
const staffCategoriesLoading = () =>
  selector('staffCategoriesLoading') as MyState['staffCategoriesLoading'];
const visibleAnybodyBooking = () =>
  selector('visibleAnybodyBooking') as MyState['visibleAnybodyBooking'];
const staffVerifiedPinCode = () =>
  selector('staffVerifiedPinCode') as MyState['staffVerifiedPinCode'];

const enableDrag = () =>
  selector('enableDrag') as MyState['enableDrag'];

const getSpecialServiceByStaffId = (id: string) =>
  useAppSelector(
    (state) =>
      specialServiceByStaffsSelect.selectById(state.appointment, id)
  );

const settingBackgrounds = () =>
  selector('settingBackgrounds') as MyState['settingBackgrounds'];

const staffFilter = () =>
  selector('staffFilter') as MyState['staffFilter'];

const paramsMasterData = () =>
  selector('paramsMasterData') as MyState['paramsMasterData'];

const summaryByStatus = () =>
  selector('summaryByStatus') as MyState['summaryByStatus'];

const appointmentSelectors = {
  getListStaffAvailableBooking,
  newAptState,
  getStaffAppointments,
  getAppointmentsLoading,
  getDateRanges,
  getDistanceTimeline,
  getViewType,
  getDateRangeList,
  getStaffList,
  getAppointmentList,
  getCalendarLoading,
  getLockBreakTimes,
  getLockBreakTimesForQuickBooking,
  getCalendarItems,
  getAppointmentLayout,
  getSelectedCalendarItemTypes,
  getParams,
  getCategories,
  getAppointmentDetailData,
  getStaffPage,
  getNewAppointmentDraftData,
  getNewBlockHourDraftData,
  getNewBreakTimeDraftData,
  getDraftAppointmentDetail,
  table,
  getAppointmentStatusFilter,
  getStaffLimit,
  getStaffTotalPage,
  anybodyBox,
  listStaffsOff,
  loadingListStaffsOff,
  getAppointmentDetailServices,
  syncTimeItem,
  multipleDetail,
  staffCategories,
  staffCategoriesLoading,
  visibleAnybodyBooking,
  staffVerifiedPinCode,
  getSpecialServiceByStaffId,
  enableDrag,
  settingBackgrounds,
  staffFilter,
  paramsMasterData,
  summaryByStatus,
};
export default appointmentSelectors;
